/**
 * Regra de habitos (spec 4.5).
 *
 * A frase da spec que define este arquivo inteiro: **"dia nao previsto nao
 * quebra a sequencia"**. Um habito de segunda/quarta/sexta nao pode perder o
 * streak porque ninguem correu no domingo — domingo simplesmente nao e um dia
 * daquele habito. O calendario e percorrido dia a dia, mas so os dias PREVISTOS
 * votam: os demais sao pulados sem contar e sem quebrar.
 *
 * A regra mora aqui, e nao em `apps/api/services`, porque os dois lados a
 * consultam: o servidor calcula os streaks com `computeHabitStreaks`, e o
 * cliente desenha a faixa da spec 9.5 com `isScheduledDate` e
 * `isHabitFulfilled` — que sao as MESMAS funcoes que decidem o que conta. Uma
 * segunda copia faria a bolinha cheia discordar do numero ao lado dela.
 *
 * O streak em si nao e recalculado no cliente ao marcar o habito: o recorde
 * vitalicio depende de um historico que ele nao tem em maos. O que muda na
 * hora e a bolinha do dia; o numero chega na invalidacao seguinte, em vez de
 * ser estimado por uma regra paralela.
 *
 * Funcoes puras, com `today` sempre injetado — nada aqui chama `Date.now()`.
 */

import type { HabitGoalType } from './constants.js';
import { addDaysToIsoDate, isoDateDifference, weekdayOfIsoDate } from './datetime.js';
import type { IsoDate } from './types.js';

/** O minimo que a regra precisa saber do habito. */
export interface HabitSchedule {
  /** Bitmask dom..sab. Bit 0 = domingo. */
  daysMask: number;
  goalType: HabitGoalType;
  goalTarget: number;
}

/** O minimo que a regra precisa saber de um registro. */
export interface HabitLogEntry {
  logDate: IsoDate;
  value: number;
}

export interface HabitStreakResult {
  currentStreak: number;
  bestStreak: number;
  /**
   * Concluidos / previstos na janela consultada, 0..1.
   *
   * Vem acompanhado dos dois contadores que o originam: uma taxa sozinha nao
   * distingue "0 de 0" de "0 de 7", e a UI precisa dessa diferenca para mostrar
   * "—" em vez de anunciar 0 % a quem nao tinha nada a fazer no periodo.
   */
  completionRate: number;
  scheduledDays: number;
  completedDays: number;
}

/** O dia da semana esta previsto no bitmask? */
export function isScheduledWeekday(daysMask: number, weekday: number): boolean {
  return (daysMask & (1 << weekday)) !== 0;
}

/** A data pura esta prevista para o habito? */
export function isScheduledDate(daysMask: number, date: IsoDate): boolean {
  return isScheduledWeekday(daysMask, weekdayOfIsoDate(date));
}

/**
 * O registro do dia cumpre a meta?
 *
 * Para `boolean`, qualquer valor a partir de 1 conta — o cliente grava 1 ao
 * marcar a caixinha. Para `count`, e preciso alcancar `goal_target`: um habito
 * de "20 paginas" com 8 paginas registradas e progresso, nao conclusao, e
 * conta-lo como dia cumprido tornaria a meta decorativa.
 */
export function isHabitFulfilled(habit: HabitSchedule, value: number): boolean {
  return habit.goalType === 'count' ? value >= habit.goalTarget : value >= 1;
}

/**
 * Streak atual, melhor streak e taxa de conclusao.
 *
 * Duas decisoes que a spec nao escreve, mas cuja alternativa tornaria o numero
 * errado na pratica:
 *
 *  1. **Hoje nao quebra nada enquanto nao acaba.** Marcar o habito as 21h e
 *     normal; se a regra tratasse "hoje ainda nao feito" como falha, o streak
 *     zeraria toda manha e so voltaria a noite. Hoje conta quando cumprido e e
 *     ignorado quando ainda nao.
 *  2. **O passado nao e reescrito.** Dias anteriores ao primeiro registro nao
 *     existem para a regra: um habito criado ontem nao carrega 400 dias de
 *     falha atras dele.
 */
export function computeHabitStreaks(
  habit: HabitSchedule,
  logs: readonly HabitLogEntry[],
  options: { today: IsoDate; from?: IsoDate; to?: IsoDate },
): HabitStreakResult {
  const cumpridos = new Set<IsoDate>();
  let primeiroRegistro: IsoDate | null = null;

  for (const log of logs) {
    // A data pura tem largura fixa, entao a ordem lexicografica e a
    // cronologica — comparar com `<` aqui e correto e evita converter tudo
    // para instante so para descobrir qual veio antes.
    if (primeiroRegistro === null || log.logDate < primeiroRegistro) {
      primeiroRegistro = log.logDate;
    }
    if (isHabitFulfilled(habit, log.value)) cumpridos.add(log.logDate);
  }

  const { today } = options;

  // ------------------------------------------------------------ streak atual
  // Anda para tras a partir de hoje. O piso e o primeiro registro: sem ele o
  // laco nunca terminaria para um habito sem dia previsto (`days_mask = 0`),
  // que jamais encontra um dia capaz de quebrar a sequencia.
  let currentStreak = 0;
  if (primeiroRegistro !== null) {
    for (let date = today; date >= primeiroRegistro; date = addDaysToIsoDate(date, -1)) {
      if (!isScheduledDate(habit.daysMask, date)) continue;

      if (cumpridos.has(date)) {
        currentStreak += 1;
        continue;
      }

      // Hoje ainda esta em aberto: pula sem contar e sem quebrar. Qualquer
      // outro dia previsto e vazio encerra a sequencia.
      if (date === today) continue;
      break;
    }
  }

  // ----------------------------------------------------------- melhor streak
  let bestStreak = 0;
  if (primeiroRegistro !== null) {
    let corrida = 0;
    for (let date = primeiroRegistro; date <= today; date = addDaysToIsoDate(date, 1)) {
      if (!isScheduledDate(habit.daysMask, date)) continue;

      if (cumpridos.has(date)) {
        corrida += 1;
        if (corrida > bestStreak) bestStreak = corrida;
        continue;
      }

      // Mesma regra do bloco acima: hoje em aberto nao derruba o recorde.
      if (date === today) break;
      corrida = 0;
    }
  }

  // ----------------------------------------------------------- taxa da janela
  // A janela e grampeada em `today`: dia previsto que ainda nao chegou nao e
  // falha, e conta-lo no denominador faria toda semana comecar em 14 %.
  const from = options.from ?? primeiroRegistro ?? today;
  const pedido = options.to ?? today;
  const to = pedido > today ? today : pedido;

  let scheduledDays = 0;
  let completedDays = 0;

  if (isoDateDifference(from, to) >= 0) {
    for (let date = from; date <= to; date = addDaysToIsoDate(date, 1)) {
      if (!isScheduledDate(habit.daysMask, date)) continue;
      scheduledDays += 1;
      if (cumpridos.has(date)) completedDays += 1;
    }
  }

  return {
    currentStreak,
    bestStreak: Math.max(bestStreak, currentStreak),
    completionRate: scheduledDays === 0 ? 0 : completedDays / scheduledDays,
    scheduledDays,
    completedDays,
  };
}

/**
 * As datas da faixa de dias da spec 9.5, terminando em `today`.
 *
 * Sete dias corridos, e nao a semana civil: o wireframe mostra hoje na ponta
 * direita, porque o que interessa e a corrida recente — numa segunda-feira, a
 * semana civil mostraria um unico dia de historico.
 */
export function habitStripDates(today: IsoDate, days = 7): IsoDate[] {
  const datas: IsoDate[] = [];
  for (let i = days - 1; i >= 0; i -= 1) datas.push(addDaysToIsoDate(today, -i));
  return datas;
}
